import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import AppSection from "@/components/app/AppSection";
import { StatusBadge } from "@/components/app/StatusBadge";
import { EmptyState } from "@/components/app/EmptyState";
import CTAButton from "@/components/app/CTAButton";
import BrandLogo from "@/components/brand/BrandLogo";
import SEO from "@/brand/SEO";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Compass, Moon, Sun, BookOpenCheck, Plus, ArrowRight, Rocket } from "lucide-react";

function fmtEur(n) {
    return `${(n || 0).toFixed(2).replace(".", ",")} €`;
}

export default function AppHome() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { mode, toggle } = useTheme();
    const isSeller = (user?.roles || []).includes("seller");
    const isBuyer = (user?.roles || []).includes("buyer");
    const [activeMission, setActiveMission] = useState(undefined); // undefined=loading, null=none, obj=loaded
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/missions/active")
            .then(({ data }) => setActiveMission(data))
            .catch((e) => { setActiveMission(null); setError(formatApiError(e)); });
    }, []);

    return (
        <>
            <SEO title="Mon aventure" description="Préparation et suivi de tes missions de complétion." noindex />
            <AppHeader
                title={`Salut ${user?.pseudo || ""}`}
                subtitle="Aventure du jour"
                left={<BrandLogo variant="compact" size="sm" to={null} />}
                right={
                    <button
                        onClick={toggle}
                        aria-label="Basculer thème"
                        data-testid="theme-toggle"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-md-surface-2"
                    >
                        {mode === "dark" ? <Sun className="h-5 w-5 text-md-text" /> : <Moon className="h-5 w-5 text-md-text" />}
                    </button>
                }
            />

            <AppSection>
                <div className="flex flex-wrap items-center gap-2">
                    {isBuyer && <StatusBadge tone="primary" testId="role-badge-buyer">Chercheur</StatusBadge>}
                    {isSeller && <StatusBadge tone="gold" testId="role-badge-seller">Dresseur</StatusBadge>}
                    {user?.seller_status === "pending" && (
                        <StatusBadge tone="warning">Onboarding vendeur — en attente</StatusBadge>
                    )}
                </div>
            </AppSection>

            <AppSection title="Ta mission">
                {error && (
                    <div className="mb-3 rounded-lg border border-md-danger/30 bg-md-danger/10 px-3 py-2 text-xs text-md-danger">{error}</div>
                )}
                {activeMission === undefined ? (
                    <div className="rounded-2xl border border-md-border bg-md-surface p-6">
                        <p className="text-sm text-md-text-2">Chargement…</p>
                    </div>
                ) : activeMission ? (
                    <button
                        onClick={() => navigate(`/app/missions/${activeMission._id}`)}
                        data-testid="home-active-mission"
                        className="group block w-full overflow-hidden rounded-2xl border border-md-secondary/40 bg-md-surface p-6 text-left transition hover:border-md-secondary"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="md-overline">EN PRÉPARATION</p>
                                <h2 className="mt-2 truncate text-xl font-black tracking-tight text-md-text">{activeMission.title}</h2>
                                <p className="mt-1 text-xs text-md-text-2">
                                    {activeMission.item_count} carte{activeMission.item_count > 1 ? "s" : ""} · {fmtEur(activeMission.total_eur)}
                                </p>
                            </div>
                            <div className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-md-secondary text-md-secondary-fg transition group-hover:scale-105">
                                <Rocket className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-md-secondary">
                            Continuer <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                        </div>
                    </button>
                ) : (
                    <EmptyState
                        icon={<BookOpenCheck className="h-6 w-6" />}
                        title="Aucune mission en cours"
                        description="Lance ta première expédition : les doubles des autres dresseurs complèteront ton classeur."
                        action={
                            <div className="flex flex-wrap justify-center gap-2">
                                <Link to="/app/missions/new" data-testid="home-cta-new-mission">
                                    <CTAButton icon={<Plus className="h-4 w-4" />}>Préparer ma mission</CTAButton>
                                </Link>
                                <Link to="/app/explorer">
                                    <CTAButton variant="outline" icon={<Compass className="h-4 w-4" />}>Explorer le catalogue</CTAButton>
                                </Link>
                            </div>
                        }
                    />
                )}
            </AppSection>
        </>
    );
}
